import type { DeepgramResponse, Keys, Language } from './types';

interface LoginResponse {
  success?: boolean;
  error?: string;
}

interface MeResponse {
  authenticated: boolean;
  loginEnabled: boolean;
}

interface UploadResponse {
  success?: boolean;
  fileId?: string;
  originalName?: string;
  error?: string;
}

export async function login(password: string): Promise<LoginResponse> {
  const res = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ password }),
  });
  return res.json();
}

export async function logout(): Promise<void> {
  await fetch('/api/logout', { method: 'POST', credentials: 'include' });
}

export async function checkAuth(): Promise<MeResponse> {
  try {
    const res = await fetch('/api/me', { credentials: 'include' });
    if (!res.ok) return { authenticated: false, loginEnabled: false };
    return res.json();
  } catch {
    return { authenticated: false, loginEnabled: false };
  }
}

export async function fetchServerKeys(): Promise<Keys | null> {
  const res = await fetch('/api/keys', { credentials: 'include' });
  if (!res.ok) return null;
  return res.json();
}

export interface ExampleFile {
  name: string;
  size: number;
}

export async function fetchExamples(): Promise<ExampleFile[]> {
  const res = await fetch('/api/examples', { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`No se pudieron listar los ejemplos (${res.status})`);
  }
  const data = (await res.json()) as { files?: ExampleFile[] };
  return data.files ?? [];
}

export async function fetchExampleBlob(name: string): Promise<Blob> {
  const res = await fetch(`/api/examples/${encodeURIComponent(name)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`No se pudo descargar el ejemplo (${res.status})`);
  }
  return res.blob();
}

export async function uploadVideo(file: File): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('video', file);
  const res = await fetch('/api/upload', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  return res.json();
}

export async function transcribe(
  apiKey: string,
  audio: Blob | File,
  language: Language
): Promise<DeepgramResponse> {
  const url = `https://api.deepgram.com/v1/listen?model=nova-2&diarize=true&language=${language}&smart_format=true&punctuate=true`;
  const contentType = audio.type || (audio instanceof File ? audio.type : 'audio/mpeg');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Token ${apiKey}`,
      'Content-Type': contentType || 'audio/mpeg',
    },
    body: audio,
  });
  if (!res.ok) {
    throw new Error(`Deepgram ${res.status} ${res.statusText}`);
  }
  return res.json();
}

const GEMINI_MODEL = 'gemini-2.5-flash';
const GEMINI_ENDPOINT = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

const GEMINI_PRICING_USD_PER_MTOK = { input: 0.3, output: 2.5 };

const geminiUsage = {
  calls: 0,
  inputTokens: 0,
  outputTokens: 0,
  costUsd: 0,
};

function reportGeminiUsage(
  promptTokens: number,
  outputTokens: number,
  thoughtsTokens: number
): void {
  const inTokensTotal = promptTokens;
  const outTokensTotal = outputTokens + thoughtsTokens;
  const callCost =
    (inTokensTotal * GEMINI_PRICING_USD_PER_MTOK.input +
      outTokensTotal * GEMINI_PRICING_USD_PER_MTOK.output) /
    1_000_000;

  geminiUsage.calls += 1;
  geminiUsage.inputTokens += inTokensTotal;
  geminiUsage.outputTokens += outTokensTotal;
  geminiUsage.costUsd += callCost;

  const fmt = (n: number): string => `$${n.toFixed(6)}`;
  const thoughtsStr = thoughtsTokens ? `+thoughts=${thoughtsTokens}` : '';
  console.log(
    `[Gemini #${geminiUsage.calls}] modelo=${GEMINI_MODEL} in=${inTokensTotal} out=${outputTokens}${thoughtsStr} coste=${fmt(callCost)} | acumulado: ${geminiUsage.calls} llamadas, ${geminiUsage.inputTokens} in + ${geminiUsage.outputTokens} out, ${fmt(geminiUsage.costUsd)}`
  );
}

interface GeminiUsageMetadata {
  promptTokenCount?: number;
  candidatesTokenCount?: number;
  thoughtsTokenCount?: number;
  totalTokenCount?: number;
}

export async function callGemini(apiKey: string, prompt: string): Promise<string> {
  const res = await fetch(`${GEMINI_ENDPOINT}?key=${apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
  });
  if (!res.ok) {
    const body = await res.text();
    console.error('Gemini error:', res.status, body);
    throw new Error(`Gemini respondió ${res.status}`);
  }
  const data = (await res.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
    usageMetadata?: GeminiUsageMetadata;
  };

  const usage = data.usageMetadata;
  if (usage) {
    reportGeminiUsage(
      usage.promptTokenCount ?? 0,
      usage.candidatesTokenCount ?? 0,
      usage.thoughtsTokenCount ?? 0
    );
  }

  return data.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
}
